// =====================================================================
// PermissionModulesTab.jsx — écran « Modules » (30-09-2026)
// =====================================================================
// Remplace le parcours « liste déroulante → une permission à la fois » par
// une manipulation directe : on choisit QUI, on voit SES modules comme des
// applications sur un téléphone, on les sort (retirer) ou on les prend sur
// l'étagère (donner).
//
// Trois décisions qui expliquent la forme de l'écran :
//   1. Aucun identifiant technique n'est affiché (règle permanente). On
//      montre les libellés français stockés en base (Permission.label).
//   2. L'écran DONNE autant qu'il RETIRE : la mesure du 30-09-2026 sur la
//      production montre que 74 des 84 dérogations existantes ajoutent un
//      droit. Un écran qui ne saurait que retirer ne remplacerait rien.
//   3. Chaque droit affiche son ORIGINE (rôle / groupe / dérogation). Sans
//      cela, on croit retirer un droit en le sortant d'un groupe alors que
//      le rôle continue de le donner.
//
// Trois types de cible, et ils n'ont PAS la même portée — c'est la chose
// la plus importante de cet écran :
//   • PERSONNES : une dérogation par personne. Réversible, nominative.
//   • GROUPE    : seulement si « aussi le groupe » est coché. Vaut aussi
//                 pour les personnes qui rejoindront le groupe plus tard.
//   • RÔLE      : touche TOUS ses porteurs — y compris ceux qu'on n'a pas
//                 cochés — et tous ceux qui recevront ce rôle à l'avenir.
//                 C'est ce que faisait l'onglet « Matrice des rôles ».
// Les rôles sont donc peints différemment, annoncés différemment, et leur
// portée réelle est calculée par le serveur avant toute écriture.
// =====================================================================

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { permissions as permsApi, errMsg } from '../../services/api.js';
import { AppIcon } from '../../components/AppIcons.jsx';
import { trierPersonnes, marqueurInactif } from '../../lib/tri-personnes.js';

// ---------------------------------------------------------------------
// Libellés + apparence des modules. La clé est la catégorie stockée en
// base ; si un module inconnu apparaît un jour, on retombe sur un libellé
// lisible plutôt que sur l'identifiant brut.
// ---------------------------------------------------------------------
const MODULES_FR = {
  audit:                { label: 'Journal d\'audit',        icon: '📜', color: '#64748b' },
  bordereaux:           { label: 'Bordereaux',              icon: '📄', color: '#0f766e', app: 'slip' },
  cahier:               { label: 'Cahier des charges',      icon: '📓', color: '#7c3aed', app: 'cahier' },
  calendar:             { label: 'Calendrier',              icon: '📅', color: '#2563eb' },
  // Commandes prêtes (SPEC v3 D, 17-10-2026) : droits propres du module (voir, encoder, charger, imprimer, administrer)
  commandes_pretes:     { label: 'Commandes prêtes',        icon: '📦', color: '#0f6e74', app: 'pallet' },
  contacts:             { label: 'Contacts',                icon: '👥', color: '#0891b2' },
  dashboards:           { label: 'Tableaux de bord',        icon: '📊', color: '#4f46e5', app: 'dash' },
  errors:               { label: 'Erreurs',                 icon: '⚠️', color: '#dc2626', app: 'alert' },
  evaluations:          { label: 'Évaluations',             icon: '⭐', color: '#d97706' },
  facturation:          { label: 'Facturation',             icon: '💶', color: '#065f46', app: 'invoice' },
  groups:               { label: 'Groupes',                 icon: '🗂️', color: '#7c2d12' },
  inventory:            { label: 'Stock',                   icon: '📦', color: '#b45309' },
  inventory_counts:     { label: 'Inventaire mensuel',      icon: '🔢', color: '#a16207', app: 'cube' },
  leaves:               { label: 'Congés',                  icon: '🏖️', color: '#0ea5e9', app: 'leave' },
  maintenance:          { label: 'Maintenance',             icon: '🔧', color: '#475569' },
  medical_certificates: { label: 'Justificatifs médicaux',  icon: '🩺', color: '#be123c', app: 'med' },
  overtime:             { label: 'Heures supplémentaires',  icon: '⏱️', color: '#9333ea', app: 'overtime' },
  pallet_tracking:      { label: 'Palettes',                icon: '🪵', color: '#78350f', app: 'pallet' },
  permissions:          { label: 'Droits d\'accès',         icon: '🔐', color: '#1d4ed8', app: 'key' },
  presence:             { label: 'Présence',                icon: '✅', color: '#15803d', app: 'presence' },
  printing_jobs:        { label: 'Impressions',             icon: '🖨️', color: '#0369a1', app: 'print' },
  projects:             { label: 'Projets',                 icon: '🏗️', color: '#c2410c', app: 'check' },
  safety:               { label: 'Sécurité',                icon: '🦺', color: '#ca8a04', app: 'shield' },
  supply_draws:         { label: 'Prélèvements',            icon: '🧰', color: '#166534', app: 'basket' },
  system:               { label: 'Système',                 icon: '⚙️', color: '#334155' },
  tasks:                { label: 'Tâches',                  icon: '🗒️', color: '#5b21b6' },
  timesheets:           { label: 'Feuilles de temps',       icon: '🕒', color: '#1e40af', app: 'clock' },
  trips:                { label: 'Trajets',                 icon: '🚚', color: '#b91c1c', app: 'truck' },
  um_dimensions:        { label: 'Dimensions & poids',      icon: '📐', color: '#3f6212', app: 'ruler' },
  users:                { label: 'Utilisateurs',            icon: '🧑‍💼', color: '#831843', app: 'users' },
};

const FALLBACK_COLORS = ['#475569', '#1d4ed8', '#b45309', '#15803d', '#9333ea', '#be123c'];

function moduleLook(category) {
  const known = MODULES_FR[category];
  if (known) return known;
  // Module inconnu : on fabrique un libellé lisible plutôt que d'afficher
  // l'identifiant technique (règle : jamais d'ID technique dans l'écran).
  const label = String(category || '')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
  let hash = 0;
  for (let i = 0; i < String(category).length; i++) hash = (hash * 31 + String(category).charCodeAt(i)) >>> 0;
  return { label, icon: '🔹', color: FALLBACK_COLORS[hash % FALLBACK_COLORS.length] };
}

// Couverture d'UN droit : « tous (12) » ou « 8 sur 12 ».
function coverageText(holders, total) {
  if (total === 0) return '—';
  if (holders >= total) return `tous (${total})`;
  if (holders === 0) return `personne (0 sur ${total})`;
  return `${holders} sur ${total}`;
}

// Couverture d'un MODULE : « possède le module » veut seulement dire « possède
// au moins un de ses droits ». Écrire « tous (12) » sur cette base ferait dire
// à la vignette que le module est couvert alors que 7 de ses 8 droits ne sont
// détenus par personne. On distingue donc « tous, entièrement » de « en partie ».
function moduleCoverageText(mod) {
  // Rôle coché sans aucun porteur : il n'y a personne à compter, mais la
  // LIGNE DE RÔLE existe et c'est elle qu'on règle. Afficher « — » laissait
  // croire qu'il n'y avait rien à faire.
  const cibles = (mod.roleTotal || 0) + (mod.groupTotal || 0);
  if (mod.total === 0 && cibles > 0) {
    const porte = (mod.roleHolders || 0) + (mod.groupHolders || 0);
    const porteTout = (mod.roleFullHolders || 0) + (mod.groupFullHolders || 0);
    const quoi = mod.roleTotal > 0 && mod.groupTotal > 0 ? 'la sélection'
      : mod.roleTotal > 0 ? 'le rôle' : 'le groupe';
    if (porteTout >= cibles) return `porté par ${quoi}`;
    if (porte > 0) return `porté en partie par ${quoi}`;
    return `pas porté par ${quoi}`;
  }
  if (mod.total === 0) return '—';
  if (mod.fullHolders >= mod.total) return `tous (${mod.total})`;
  if (mod.holders >= mod.total) return `tous, en partie (${mod.total})`;
  if (mod.holders === 0) return `personne (0 sur ${mod.total})`;
  return `${mod.holders} sur ${mod.total}, en partie`;
}
function moduleFullyCovered(mod) {
  const cibles = (mod.roleTotal || 0) + (mod.groupTotal || 0);
  if (mod.total === 0 && cibles > 0) {
    return (mod.roleFullHolders || 0) + (mod.groupFullHolders || 0) >= cibles;
  }
  return mod.total > 0 && mod.fullHolders >= mod.total;
}
// « Ce module est-il possédé ? » — par des personnes, ou par une ligne de
// rôle / de groupe cochée. Sans ces deux derniers cas, un rôle que personne
// n'occupe encore, ou un groupe dont tous les membres ont une dérogation
// « refusé », n'aurait aucun module à gauche : tout partirait sur l'étagère,
// y compris ce qu'il porte réellement.
function moduleHeld(mod) {
  return mod.holders > 0 || (mod.roleHolders || 0) > 0 || (mod.groupHolders || 0) > 0;
}

// Origine d'un droit, en clair. C'est l'information qui évite la fausse
// manœuvre « je l'enlève du groupe » alors que le rôle le donne aussi.
function sourceText(perm, cibleLabels = new Map()) {
  const parts = [];
  if (perm.sources.role > 0)     parts.push(`${perm.sources.role} par leur rôle`);
  if (perm.sources.group > 0)    parts.push(`${perm.sources.group} par un groupe`);
  if (perm.sources.override > 0) parts.push(`${perm.sources.override} par une dérogation`);
  // La ligne de rôle existe même sans porteur : c'est ce qu'on règle quand on
  // prépare un rôle avant d'y affecter quelqu'un.
  const portesPar = (perm.roleIds || []).map((r) => cibleLabels.get(r) || r);
  if (portesPar.length) parts.push(`porté par le rôle ${portesPar.join(', ')}`);
  const parGroupe = (perm.groupIds || []).map((g) => cibleLabels.get(g) || g);
  if (parGroupe.length) parts.push(`porté par le groupe ${parGroupe.join(', ')}`);
  return parts.length ? parts.join(' · ') : 'personne ne l\'a';
}

// Un rôle ne se présente pas comme un groupe : couleur, pictogramme et mot
// différents, pour qu'on ne coche jamais l'un en croyant cocher l'autre.
const ROLE_TONE = { bg: '#fff7ed', border: '#fdba74', fg: '#9a3412', strong: '#7c2d12' };

export default function PermissionModulesTab() {
  // --- Sources de la colonne « Qui » ----------------------------------
  const [sources, setSources] = useState({ roles: [], groups: [], users: [] });
  const [sourcesError, setSourcesError] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState('');

  // --- Vue « modules de la sélection » --------------------------------
  const [view, setView] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  // --- Manipulation ----------------------------------------------------
  const [removeMode, setRemoveMode] = useState(false);
  const [openedCategory, setOpenedCategory] = useState(null);
  const [dragZone, setDragZone] = useState(null);      // 'remove' | 'grant' | null
  const [flash, setFlash] = useState(null);            // message court, non bloquant

  // --- Confirmation ----------------------------------------------------
  const [pending, setPending] = useState(null);        // { mode, permissionIds, title }
  const requestSeq = useRef(0);
  const flashTimer = useRef(null);

  // Le minuteur du message court survivrait au changement d'onglet et
  // appellerait setFlash sur un composant démonté.
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);

  useEffect(() => {
    permsApi.getSelectionSources()
      .then((d) => { setSources({ roles: [], groups: [], users: [], ...d }); setSourcesError(null); })
      .catch((e) => setSourcesError(errMsg(e)));
  }, []);

  const hasSelection = selectedRoleIds.length > 0 || selectedGroupIds.length > 0 || selectedUserIds.length > 0;
  const selectedRoles = useMemo(
    () => sources.roles.filter((r) => selectedRoleIds.includes(r.id)), [sources.roles, selectedRoleIds]);
  // Rôles ET groupes : l'écran n'affiche jamais un identifiant technique, et
  // les deux en portent un.
  const cibleLabels = useMemo(() => new Map([
    ...sources.roles.map((r) => [r.id, r.label]),
    ...sources.groups.map((g) => [g.id, g.name]),
  ]), [sources.roles, sources.groups]);

  const reloadView = useCallback(async () => {
    // Le compteur DOIT être incrémenté ici aussi : sans cela, une réponse
    // partie avant que la sélection soit vidée revient, se croit à jour et
    // réaffiche les chiffres de l'ancienne sélection — puis l'opération
    // serait calculée sur la mauvaise population.
    const seq = ++requestSeq.current;
    if (!hasSelection) {
      setView(null); setViewError(null); setViewLoading(false); setOpenedCategory(null);
      return;
    }
    setViewLoading(true);
    setView(null);              // on n'affiche jamais les chiffres d'avant
    setOpenedCategory(null);
    try {
      const d = await permsApi.getModulesView({
        userIds: selectedUserIds, groupIds: selectedGroupIds, roleIds: selectedRoleIds,
      });
      // Une réponse arrivée en retard ne doit pas écraser une sélection plus récente.
      if (seq !== requestSeq.current) return;
      setView(d);
      setViewError(null);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setViewError(errMsg(e));
      setView(null);
    } finally {
      if (seq === requestSeq.current) setViewLoading(false);
    }
  }, [hasSelection, selectedUserIds, selectedGroupIds, selectedRoleIds]);

  useEffect(() => { reloadView(); }, [reloadView]);

  // Les personnes réellement concernées = membres des groupes cochés ∪
  // personnes cochées. Le serveur fait foi (il connaît les groupes
  // secondaires) ; avant sa réponse on affiche une estimation locale.
  // Estimation locale, le temps que le serveur réponde. Elle est restreinte
  // aux personnes que cet écran connaît par leur nom : l'effectif d'un groupe
  // compte aussi ses membres désactivés, qui ne figurent pas dans la liste à
  // cocher. Sans ce filtre, le compteur annonçait 12 au-dessus d'une liste de
  // 10 noms pendant toute la durée du calcul.
  const localPeople = useMemo(() => {
    const known = new Set(sources.users.map((u) => u.id));
    const ids = new Set(selectedUserIds.filter((id) => known.has(id)));
    const byId = new Map(sources.groups.map((g) => [g.id, g]));
    for (const gid of selectedGroupIds) {
      for (const uid of (byId.get(gid)?.memberIds || [])) if (known.has(uid)) ids.add(uid);
    }
    const roleById = new Map(sources.roles.map((r) => [r.id, r]));
    for (const rid of selectedRoleIds) {
      for (const uid of (roleById.get(rid)?.memberIds || [])) if (known.has(uid)) ids.add(uid);
    }
    return ids;
  }, [selectedUserIds, selectedGroupIds, selectedRoleIds, sources.groups, sources.roles, sources.users]);

  const people = view?.people || sources.users.filter((u) => localPeople.has(u.id));
  const peopleCount = view ? view.peopleCount : localPeople.size;
  const superAdmins = trierPersonnes(view?.superAdmins || people.filter((p) => p.isSuperAdmin));
  // Une seule fois par sélection : ce composant se redessine à chaque frappe
  // dans la recherche, et la liste peut compter une centaine de personnes.
  const nomsConcernes = useMemo(
    () => trierPersonnes(people).map((p) => p.name + marqueurInactif(p)).join(' · '), [people]);
  // Base de comptage : les personnes sur lesquelles l'ecran peut agir.
  // Les comptes a acces total en sont exclus (une restriction n'a aucun effet
  // sur eux) - sinon l'etagere paraitrait vide et les couvertures seraient fausses.
  const actionableCount = view ? view.total : localPeople.size;

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sources.users;
    return sources.users.filter((u) =>
      u.name.toLowerCase().includes(q)
      || (u.username || '').toLowerCase().includes(q)
      || (u.groupName || '').toLowerCase().includes(q));
  }, [sources.users, search]);

  // Tri sur le NOM FRANÇAIS affiché. Trier sur la catégorie technique mettrait
  // « Stock » (inventory) avant « Congés » (leaves) : incompréhensible sur un
  // écran dont la règle est qu'aucun identifiant technique n'apparaît.
  const byFrenchLabel = (a, b) => moduleLook(a.category).label.localeCompare(moduleLook(b.category).label, 'fr');
  const heldModules = useMemo(
    () => (view?.modules || []).filter(moduleHeld).sort(byFrenchLabel), [view]);
  const shelfModules = useMemo(
    () => (view?.modules || []).filter((m) => !moduleHeld(m)).sort(byFrenchLabel), [view]);

  const openedModule = useMemo(
    () => (view?.modules || []).find((m) => m.category === openedCategory) || null,
    [view, openedCategory]);

  function toggleRole(id) {
    setSelectedRoleIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }
  function toggleGroup(id) {
    setSelectedGroupIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }
  function toggleUser(id) {
    setSelectedUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }
  function clearSelection() {
    setSelectedRoleIds([]); setSelectedGroupIds([]); setSelectedUserIds([]);
    setOpenedCategory(null); setRemoveMode(false);
  }

  // Le message court a SON propre délai. Sans annuler le précédent, deux
  // messages rapprochés partagent le premier minuteur : le second disparaît
  // presque aussitôt et l'utilisateur ne lit jamais l'explication du refus.
  function showFlash(message, tone = 'info') {
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    setFlash({ message, tone });
    flashTimer.current = window.setTimeout(() => { flashTimer.current = null; setFlash(null); }, 6000);
  }

  // -------------------------------------------------------------------
  // Demandes de modification. Rien n'est écrit ici : on ouvre l'écran de
  // confirmation, qui va chercher le plan réel auprès du serveur.
  // -------------------------------------------------------------------
  function askRemoveModule(mod) {
    if (!hasSelection) { showFlash('Choisissez d\'abord des personnes ou des groupes.', 'warn'); return; }
    // Un module « verrouillé » n'est pas intouchable EN ENTIER : seuls ses
    // droits socles le sont. Bloquer la vignette rendait inaccessible le
    // retrait d'un droit d'administration des feuilles, pourtant légitime et
    // accepté par le serveur — la promesse faite dans la confirmation.
    const removable = mod.permissions.filter(
      (p) => !p.lockedReason && (p.holders > 0 || (p.roleIds || []).length > 0));
    if (removable.length === 0) {
      showFlash('Rien à retirer dans ce module pour cette sélection.', 'warn');
      return;
    }
    setPending({
      mode: 'REMOVE',
      permissionIds: removable.map((p) => p.id),
      title: `Retirer le module « ${moduleLook(mod.category).label} »`,
      category: mod.category,
    });
  }

  function askGrantModule(mod) {
    if (!hasSelection) { showFlash('Choisissez d\'abord des personnes ou des groupes.', 'warn'); return; }
    const missing = mod.permissions.filter(
      (p) => p.holders < p.total
        || selectedRoleIds.some((r) => !(p.roleIds || []).includes(r)));
    if (missing.length === 0) {
      showFlash('Cette sélection possède déjà tout ce module.', 'warn');
      return;
    }
    setPending({
      mode: 'GRANT',
      permissionIds: missing.map((p) => p.id),
      title: `Donner le module « ${moduleLook(mod.category).label} »`,
      category: mod.category,
      // Le geste « + » n'est pas l'exact inverse du « ✕ » : retirer ne touche
      // que ce que les gens ont, donner donne TOUT le module. C'est utile pour
      // installer un module, dangereux comme annulation. On le dit.
      wholeModule: true,
    });
  }

  function askOnePermission(mod, perm, mode) {
    if (!hasSelection) { showFlash('Choisissez d\'abord des personnes ou des groupes.', 'warn'); return; }
    if (mode === 'REMOVE' && perm.lockedReason) { showFlash(perm.lockedReason, 'warn'); return; }
    setPending({
      mode,
      // Tout le monde l'a déjà : l'opération ne vaut plus que pour le groupe.
      // « Plus rien à faire sur les personnes » : tout le monde l'a déjà ET
      // tous les rôles cochés le portent déjà. Sans la seconde condition, on
      // proposait de cocher « aussi le groupe » pour une opération qui avait
      // encore un rôle à modifier.
      groupOnly: mode === 'GRANT'
        && perm.total > 0 && perm.holders >= perm.total
        && selectedRoleIds.every((r) => (perm.roleIds || []).includes(r))
        && selectedGroupIds.length > 0,
      permissionIds: [perm.id],
      title: `${mode === 'REMOVE' ? 'Retirer' : 'Donner'} « ${perm.label} »`,
      category: mod.category,
    });
  }

  // -------------------------------------------------------------------
  // Glisser-déposer. Les tuiles portent la catégorie ; la zone qui reçoit
  // décide de l'action. Le clavier reste possible : chaque tuile a aussi
  // ses boutons (✕ en mode retrait, + sur l'étagère).
  // -------------------------------------------------------------------
  function onTileDragStart(e, category, from) {
    e.dataTransfer.setData('text/plain', JSON.stringify({ category, from }));
    e.dataTransfer.effectAllowed = 'move';
  }
  function readDrag(e) {
    try { return JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return null; }
  }
  // La corbeille est DANS le panneau « Leurs modules », qui est lui-même une
  // zone de dépôt (pour l'inverse : donner). Sans stopPropagation, déposer une
  // vignette de l'étagère sur la corbeille remonterait au parent et ouvrirait
  // « Donner » — l'exact contraire du geste fait.
  function onDropRemove(e) {
    e.preventDefault(); e.stopPropagation(); setDragZone(null);
    const d = readDrag(e);
    if (!d || d.from !== 'held') return;
    const mod = (view?.modules || []).find((m) => m.category === d.category);
    if (mod) askRemoveModule(mod);
  }
  function onDropGrant(e) {
    e.preventDefault(); setDragZone(null);
    const d = readDrag(e);
    if (!d || d.from !== 'shelf') return;
    const mod = (view?.modules || []).find((m) => m.category === d.category);
    if (mod) askGrantModule(mod);
  }

  // =====================================================================
  // Rendu
  // =====================================================================
  return (
    <div>
      {sourcesError && <Banner tone="error">Impossible de charger les groupes et les personnes : {sourcesError}</Banner>}
      {flash && <Banner tone={flash.tone}>{flash.message}</Banner>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 340px) 1fr', gap: 16, alignItems: 'start' }}>

        {/* ---------------- A. QUI ---------------------------------- */}
        <div style={panel}>
          <PanelTitle>Qui</PanelTitle>

          {/* Les rôles d'abord, et dans leur propre encadré : leur portée
              n'est pas celle d'un groupe, et les aligner sur la même liste
              ferait cocher l'un pour l'autre. */}
          <div style={{
            marginTop: 6, padding: 8, borderRadius: 8,
            background: ROLE_TONE.bg, border: `1px dashed ${ROLE_TONE.border}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: ROLE_TONE.strong, marginBottom: 2 }}>
              🎭 Rôles — portée la plus large
            </div>
            <div style={{ fontSize: 11, color: ROLE_TONE.fg, marginBottom: 6, lineHeight: 1.45 }}>
              Modifier un rôle touche <strong>toutes</strong> les personnes qui le portent,
              même celles qui ne sont pas cochées ici, <strong>et toutes celles qui le
              recevront plus tard</strong>. Ce n'est pas une dérogation : c'est le réglage
              du rôle lui-même.
            </div>
            {sources.roles.filter((r) => r.editable).map((r) => (
              <label key={r.id} style={{ ...checkRow, padding: '3px 2px' }}>
                <input type="checkbox" checked={selectedRoleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
                <span style={{ flex: 1, color: ROLE_TONE.strong, fontWeight: selectedRoleIds.includes(r.id) ? 700 : 500 }}>
                  {r.label}
                </span>
                <span
                  style={{ ...countPill, background: '#ffedd5', color: ROLE_TONE.fg }}
                  title={r.inactiveCount > 0 ? `${r.inactiveCount} compte(s) désactivé(s) inclus` : undefined}
                >
                  {r.memberCount} pers.
                </span>
              </label>
            ))}
            {sources.roles.some((r) => !r.editable) && (
              <div style={{ fontSize: 11, color: ROLE_TONE.fg, marginTop: 4 }}>
                « Accès total » n'est pas proposé : ces comptes ont tous les droits par
                construction, aucun réglage ne les concerne.
              </div>
            )}
            {sources.unknownRoleCount > 0 && (
              // Le serveur compte les comptes dont le rôle n'est pas dans la
              // liste ci-dessus. Les taire laisserait croire que cocher tous
              // les rôles couvre tout le monde — ce serait faux.
              <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>
                ⚠️ {sources.unknownRoleCount} compte{sources.unknownRoleCount > 1 ? 's portent' : ' porte'} un
                rôle que l'application ne connaît pas : {sources.unknownRoleCount > 1 ? 'ils ne sont' : 'il n’est'} couvert
                {sources.unknownRoleCount > 1 ? 's' : ''} par aucune case ci-dessus. Sélectionnez {sources.unknownRoleCount > 1 ? 'ces personnes' : 'cette personne'} par leur nom.
              </div>
            )}
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', margin: '14px 0 6px' }}>Groupes</div>
          {sources.groups.length === 0 && <div style={muted}>Aucun groupe actif.</div>}
          {sources.groups.map((g) => (
            <label key={g.id} style={checkRow}>
              <input type="checkbox" checked={selectedGroupIds.includes(g.id)} onChange={() => toggleGroup(g.id)} />
              <span style={{ flex: 1 }}>{g.icon ? `${g.icon} ` : ''}{g.name}</span>
              {/* L'effectif inclut les comptes désactivés, comme côté serveur :
                  deux chiffres différents pour le même groupe seraient pires
                  qu'un chiffre à expliquer. */}
              <span style={countPill} title={g.inactiveCount > 0 ? `${g.inactiveCount} compte(s) désactivé(s) inclus` : undefined}>
                {g.memberCount} pers.{g.inactiveCount > 0 ? ` (dont ${g.inactiveCount} inactif${g.inactiveCount > 1 ? 's' : ''})` : ''}
              </span>
            </label>
          ))}

          <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', margin: '14px 0 6px' }}>Personnes</div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un nom, un groupe…"
            style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
          />
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
            {filteredUsers.length === 0 && <div style={{ ...muted, padding: 10 }}>Aucune personne ne correspond à « {search} ».</div>}
            {filteredUsers.map((u) => (
              <label key={u.id} style={{ ...checkRow, padding: '5px 8px' }}>
                <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUser(u.id)} />
                <span style={{ flex: 1 }}>
                  {u.name}
                  {u.groupName && <span style={{ color: '#94a3b8' }}> · {u.groupName}</span>}
                </span>
                {u.isSuperAdmin && <span style={{ ...countPill, background: '#fef3c7', color: '#78350f' }}>accès total</span>}
              </label>
            ))}
          </div>

          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 600, color: peopleCount ? '#1e293b' : '#94a3b8' }}>
              {peopleCount} personne{peopleCount > 1 ? 's' : ''} sélectionnée{peopleCount > 1 ? 's' : ''}
              {viewLoading && <span style={{ color: '#94a3b8', fontWeight: 400 }}> · calcul…</span>}
            </div>
            {peopleCount > 0 && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                {nomsConcernes}
              </div>
            )}
            {hasSelection && (
              <button onClick={clearSelection} style={{ ...btn, marginTop: 8 }}>Tout décocher</button>
            )}
          </div>
        </div>

        {/* ---------------- B + C. MODULES --------------------------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {!hasSelection && (
            <div style={{ ...panel, textAlign: 'center', padding: 36, color: '#64748b' }}>
              <div style={{ fontSize: 34, marginBottom: 8 }}>👈</div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Commencez par choisir qui est concerné</div>
              <div>Cochez un groupe ou des personnes à gauche. Tant que personne n'est sélectionné,
                rien ne peut être retiré ni donné — c'est volontaire : une action sans destinataire
                ne veut rien dire.</div>
            </div>
          )}

          {viewError && <Banner tone="error">Impossible de calculer les droits : {viewError}</Banner>}

          {selectedRoles.length > 0 && (
            <div style={{
              background: ROLE_TONE.bg, border: `2px solid ${ROLE_TONE.border}`,
              color: ROLE_TONE.strong, padding: '10px 12px', borderRadius: 8, fontSize: 13,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                🎭 Vous travaillez sur {selectedRoles.length > 1 ? 'des rôles' : 'un rôle'} :{' '}
                {selectedRoles.map((r) => r.label).join(' · ')}
              </div>
              {(view?.roleReach || []).map((r) => (
                <div key={r.id} style={{ marginTop: 2 }}>
                  <strong>{r.label}</strong> : {r.holders} personne{r.holders > 1 ? 's' : ''} le porte{r.holders > 1 ? 'nt' : ''} aujourd'hui
                  {r.notPicked > 0 && <>, dont <strong>{r.notPicked} que vous n'avez pas cochée{r.notPicked > 1 ? 's' : ''}</strong></>}.
                </div>
              ))}
              <div style={{ marginTop: 4 }}>
                Toute modification s'appliquera aussi aux personnes qui recevront ce rôle
                à l'avenir. Pour ne toucher qu'une partie des gens, décochez le rôle et
                cochez les personnes ou leur groupe.
              </div>
            </div>
          )}

          {hasSelection && superAdmins.length > 0 && (
            <Banner tone="warn">
              <strong>{superAdmins.length === 1 ? 'Une personne a un accès total' : `${superAdmins.length} personnes ont un accès total`}</strong>
              {' '}({superAdmins.map((s) => s.name).join(', ')}).
              Pour {superAdmins.length === 1 ? 'elle' : 'elles'}, retirer un droit <strong>n'a aucun effet</strong> :
              l'accès total passe au-dessus de toutes les restrictions. {superAdmins.length === 1 ? 'Elle sera ignorée' : 'Elles seront ignorées'} par l'opération,
              et {superAdmins.length === 1 ? 'elle ne compte pas' : 'elles ne comptent pas'} dans les couvertures affichées ci-dessous.
            </Banner>
          )}

          {hasSelection && view?.selfInSelection && (
            <Banner tone="warn">
              <strong>Vous êtes dans cette sélection.</strong> Vos propres droits ne sont pas
              comptés dans les chiffres ci-dessous, et <strong>aucune dérogation individuelle</strong>{' '}
              ne sera écrite sur vous : on ne se retire — ni ne s'accorde — un droit personnel
              depuis cet écran. En revanche, si vous modifiez un <strong>groupe dont vous êtes
              membre</strong>, cela vous touchera aussi ; la fenêtre de confirmation vous le dira
              avant d'appliquer.
            </Banner>
          )}

          {/* Un seul bloc, mais TROIS phrases possibles : « personne »,
              « que vous », « que des accès totaux » — et le cas mixte, qui
              existe aussi. Deux conditions exclusives se trompaient dès qu'on
              se sélectionnait soi-même en même temps qu'un accès total. */}
          {hasSelection && view && actionableCount === 0 && selectedRoleIds.length === 0 && (
            <EmptySelectionPanel
              peopleCount={peopleCount}
              superAdminCount={superAdmins.length}
              selfInSelection={!!view.selfInSelection}
              groupsSelected={selectedGroupIds.length > 0}
            />
          )}

          {/* Un rôle sans aucun porteur reste réglable : c'est la ligne de rôle
              qu'on modifie, et elle vaudra pour la première personne qui le
              recevra. Masquer l'éditeur ici, c'était perdre ce que faisait
              l'ancienne matrice des rôles. */}
          {hasSelection && view && actionableCount === 0 && selectedRoleIds.length > 0 && (
            <Banner tone="info">
              <strong>Personne ne porte {selectedRoles.length > 1 ? 'ces rôles' : 'ce rôle'} aujourd'hui.</strong>{' '}
              Vous réglez donc le rôle lui-même : ce réglage s'appliquera à la première personne
              qui le recevra. Les compteurs « qui l'a » restent à zéro, c'est normal.
            </Banner>
          )}

          {hasSelection && view && (actionableCount > 0 || selectedRoleIds.length > 0) && (
            <>
              {/* ---- B. Leurs modules ---- */}
              <div
                style={{ ...panel, outline: dragZone === 'grant' ? '2px dashed #16a34a' : 'none' }}
                onDragOver={(e) => { e.preventDefault(); setDragZone('grant'); }}
                onDragLeave={() => setDragZone(null)}
                onDrop={onDropGrant}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <PanelTitle>Leurs modules</PanelTitle>
                  <span style={muted}>{heldModules.length} module{heldModules.length > 1 ? 's' : ''}</span>
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() => setRemoveMode((v) => !v)}
                    style={{ ...btn, background: removeMode ? '#fee2e2' : 'white', borderColor: removeMode ? '#fca5a5' : '#cbd5e1', color: removeMode ? '#991b1b' : '#334155' }}
                  >
                    {removeMode ? '✓ Terminer le mode retrait' : '✕ Mode retrait'}
                  </button>
                </div>
                <div style={{ ...muted, marginBottom: 10 }}>
                  Glissez une vignette vers « Retirer », ou activez le mode retrait pour faire
                  apparaître les ✕. Cliquez une vignette pour ouvrir le détail des droits.
                </div>

                {heldModules.length === 0 && (
                  <div style={{ ...muted, padding: 14 }}>
                    Cette sélection ne possède aucun module. Prenez-en un sur l'étagère ci-dessous.
                  </div>
                )}

                <div style={tileGrid}>
                  {heldModules.map((mod) => (
                    <ModuleTile
                      key={mod.category}
                      mod={mod}
                      opened={openedCategory === mod.category}
                      removeMode={removeMode}
                      onOpen={() => setOpenedCategory(openedCategory === mod.category ? null : mod.category)}
                      onRemove={() => askRemoveModule(mod)}
                      onDragStart={(e) => onTileDragStart(e, mod.category, 'held')}
                    />
                  ))}
                </div>

                {/* Zone « Retirer » */}
                <div
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragZone('remove'); }}
                  onDragLeave={(e) => { e.stopPropagation(); setDragZone(null); }}
                  onDrop={onDropRemove}
                  style={{
                    marginTop: 14, padding: '18px 12px', textAlign: 'center', borderRadius: 10,
                    border: `2px dashed ${dragZone === 'remove' ? '#dc2626' : '#e2e8f0'}`,
                    background: dragZone === 'remove' ? '#fef2f2' : '#f8fafc',
                    color: dragZone === 'remove' ? '#991b1b' : '#94a3b8', fontWeight: 600,
                  }}
                >
                  🗑️ Retirer — déposez ici une vignette
                </div>
              </div>

              {/* ---- Détail d'un module ouvert ---- */}
              {openedModule && (
                <ModuleDetail
                  mod={openedModule}
                  groupsSelected={selectedGroupIds.length > 0}
                  onClose={() => setOpenedCategory(null)}
                  cibleLabels={cibleLabels}
                  selectedRoleIds={selectedRoleIds}
                  selectedGroupIds={selectedGroupIds}
                  onRemove={(perm) => askOnePermission(openedModule, perm, 'REMOVE')}
                  onGrant={(perm) => askOnePermission(openedModule, perm, 'GRANT')}
                />
              )}

              {/* ---- C. Étagère ---- */}
              <div style={panel}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <PanelTitle>Modules disponibles</PanelTitle>
                  <span style={muted}>{shelfModules.length} module{shelfModules.length > 1 ? 's' : ''}</span>
                </div>
                <div style={{ ...muted, marginBottom: 10 }}>
                  Ce que cette sélection n'a pas encore. Glissez une vignette vers « Leurs modules »,
                  ou utilisez le +. C'est le geste le plus fréquent : la quasi-totalité des
                  dérogations existantes <strong>ajoutent</strong> un droit.
                </div>

                {shelfModules.length === 0 && (
                  <div style={{ ...muted, padding: 14 }}>
                    Cette sélection possède déjà au moins un droit dans chaque module.
                    Pour compléter un module partiellement couvert, ouvrez-le ci-dessus.
                  </div>
                )}

                <div style={tileGrid}>
                  {shelfModules.map((mod) => (
                    <ModuleTile
                      key={mod.category}
                      mod={mod}
                      shelf
                      opened={openedCategory === mod.category}
                      onOpen={() => setOpenedCategory(openedCategory === mod.category ? null : mod.category)}
                      onGrant={() => askGrantModule(mod)}
                      onDragStart={(e) => onTileDragStart(e, mod.category, 'shelf')}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {pending && (
        <ConfirmDialog
          pending={pending}
          view={view}
          groups={sources.groups.filter((g) => selectedGroupIds.includes(g.id))}
          roles={selectedRoles}
          selectedUserIds={selectedUserIds}
          selectedGroupIds={selectedGroupIds}
          selectedRoleIds={selectedRoleIds}
          onCancel={() => setPending(null)}
          onDone={async (message) => {
            setPending(null);
            showFlash(message, 'success');
            await reloadView();
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Aucune personne sur laquelle agir : dire EXACTEMENT laquelle des raisons
// s'applique. « Cette sélection ne contient que des comptes à accès total »
// alors que l'utilisateur s'y est mis lui-même est faux, et sur cet écran
// une phrase fausse coûte plus cher qu'une phrase absente.
// =====================================================================
function EmptySelectionPanel({ peopleCount, superAdminCount, selfInSelection, groupsSelected }) {
  let icon = '🫥';
  let title = 'Cette sélection ne contient aucune personne active';
  let body = "Le groupe choisi n'a pas de membre actif. Choisissez un autre groupe, ou des personnes directement.";

  if (peopleCount > 0) {
    // L'auteur est aujourd'hui toujours un super-administrateur (la page leur
    // est réservée) : exiger « aucun accès total » rendait ce cas injoignable
    // et affichait « vous-même ET des comptes à accès total » pour une
    // sélection d'une seule personne.
    const onlySelf = selfInSelection && peopleCount === 1;
    const onlySuperAdmins = !selfInSelection && superAdminCount === peopleCount;
    if (onlySelf) {
      icon = '🙋';
      title = 'Cette sélection ne contient que vous';
      body = "On ne modifie pas ses propres droits depuis cet écran — on s'y couperait l'accès sans pouvoir revenir en arrière. Sélectionnez d'autres personnes, ou demandez à quelqu'un d'autre.";
    } else if (onlySuperAdmins) {
      icon = '🛡️';
      title = 'Cette sélection ne contient que des comptes à accès total';
      body = "Il n'y a rien à retirer ni à donner : ces comptes ont déjà tout, et aucune restriction ne s'y applique.";
    } else {
      icon = '🛡️';
      title = 'Personne, ici, ne peut être modifié depuis cet écran';
      body = "La sélection ne contient que vous-même et des comptes à accès total. Choisissez quelqu'un d'autre pour agir.";
    }
  }

  return (
    <div style={{ ...panel, textAlign: 'center', padding: 30, color: '#64748b' }}>
      <div style={{ fontSize: 30, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>{title}</div>
      <div>{body}</div>
      {groupsSelected && (
        // Cet écran travaille sur des PERSONNES : sans personne à modifier, il
        // n'a rien à proposer. Les droits du groupe lui-même — ceux qui
        // s'appliqueront à ses futurs membres — se règlent ailleurs, et il vaut
        // mieux le dire que laisser l'utilisateur devant un écran muet.
        <div style={{ marginTop: 10, fontSize: 12 }}>
          Pour régler les droits <strong>du groupe lui-même</strong> (ceux que recevront
          les personnes qui le rejoindront), passez par <em>Administration → Groupes →
          onglet Permissions</em>.
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Vignette de module — carré arrondi coloré + nom français + couverture
// =====================================================================
function ModuleTile({ mod, shelf = false, opened, removeMode = false, onOpen, onRemove, onGrant, onDragStart }) {
  const look = moduleLook(mod.category);
  const locked = !!mod.locked;
  return (
    <div style={{ position: 'relative', width: 104, textAlign: 'center' }}>
      <button
        type="button"
        draggable
        onDragStart={onDragStart}
        onClick={onOpen}
        // Un module « verrouillé » n'est pas intouchable : seuls ses deux
        // droits socles le sont. Interdire le glisser-déposer donnait deux
        // réponses différentes au même geste — la croix marchait, le glisser
        // non — et l'infobulle affirmait l'inverse de ce que fait le serveur.
        title={shelf
          ? 'Aucune personne de la sélection ne possède ce module — cliquez pour voir ses droits'
          : locked
            ? 'Le socle « voir » et « créer » ses propres feuilles reste, le reste du module est retirable'
            : 'Ouvrir le détail des droits'}
        style={{
          position: 'relative',
          width: 72, height: 72, borderRadius: 18, border: opened ? '3px solid #1e293b' : '1px solid rgba(0,0,0,.12)',
          // Avec l'icone du menu principal (multicolore), le fond reste clair
          // comme dans le launcher : c'est ce qui rend le module reconnaissable
          // d'un ecran a l'autre. Sans icone du menu, on garde la pastille coloree.
          background: look.app ? '#ffffff' : look.color,
          color: look.app ? '#3f4654' : 'white',
          fontSize: 30, lineHeight: '1', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: look.app ? '0 4px 14px rgba(60,50,90,0.10)' : '0 2px 6px rgba(15,23,42,.18)',
        }}
      >
        {/* Un module que la selection n'a pas garde ses vraies couleurs : c'est
            la meme image que dans le menu, donc on le reconnait aussi vite ici.
            Le gris effacait justement ce qu'on venait de rendre reconnaissable.
            L'absence de droit se dit par une barre rouge, comme un panneau
            d'interdiction. Le trait blanc dessous la garde lisible meme quand
            l'icone est elle-meme rouge (Erreurs, premiers soins). */}
        <span style={{ display: 'flex' }}>
          {look.app ? <AppIcon name={look.app} size={42} /> : look.icon}
        </span>
        {shelf && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="72" height="72" viewBox="0 0 72 72">
              <line x1="14" y1="58" x2="58" y2="14" stroke="#ffffff" strokeWidth="7" strokeLinecap="round" opacity="0.8" />
              <line x1="14" y1="58" x2="58" y2="14" stroke="#dc2626" strokeWidth="4.5" strokeLinecap="round" />
            </svg>
          </span>
        )}
      </button>

      {/* ✕ façon désinstallation, uniquement en mode retrait */}
      {removeMode && !shelf && (
        <button
          type="button"
          onClick={onRemove}
          title={locked
            ? 'Retire les droits d\'administration de ce module ; les droits « ses propres feuilles » restent'
            : 'Retirer ce module'}
          style={{
            position: 'absolute', top: -6, left: 8, width: 22, height: 22, borderRadius: 11,
            border: '1px solid white', background: '#dc2626', color: 'white',
            cursor: 'pointer', fontSize: 13, lineHeight: '20px', padding: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,.3)',
          }}
        >
          ✕
        </button>
      )}

      {shelf && (
        <button
          type="button"
          onClick={onGrant}
          title="Donner ce module à la sélection"
          style={{
            position: 'absolute', top: -6, right: 8, width: 22, height: 22, borderRadius: 11,
            border: '1px solid white', background: '#16a34a', color: 'white',
            cursor: 'pointer', fontSize: 15, lineHeight: '20px', padding: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,.3)',
          }}
        >
          +
        </button>
      )}

      <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: 6, lineHeight: 1.2 }}>
        {look.label}
      </div>
      <div style={{ fontSize: 11, color: moduleFullyCovered(mod) ? '#15803d' : '#64748b' }}>
        {moduleCoverageText(mod)}
      </div>
      {locked && <div style={{ fontSize: 10, color: '#92400e' }}>🔒 socle protégé</div>}
    </div>
  );
}

// =====================================================================
// Détail d'un module : la liste des droits, leur origine, et pour chacun
// « Retirer » / « Donner ». C'est ici qu'on agit sur UN droit précis.
// =====================================================================
function ModuleDetail({ mod, onClose, onRemove, onGrant, groupsSelected, cibleLabels = new Map(), selectedRoleIds = [], selectedGroupIds = [] }) {
  const look = moduleLook(mod.category);
  return (
    <div style={{ ...panel, borderLeft: `4px solid ${look.color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22, display: 'flex', alignItems: 'center' }}>
          {look.app ? <AppIcon name={look.app} size={24} /> : look.icon}
        </span>
        <PanelTitle>{look.label}</PanelTitle>
        <span style={muted}>{moduleCoverageText(mod)}</span>
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={btn}>Fermer</button>
      </div>

      {mod.locked && (
        <div style={{ ...noteBox, background: '#fffbeb', borderColor: '#fcd34d', color: '#78350f' }}>
          🔒 {mod.lockedReason}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 }}>
        <thead style={{ background: '#f8fafc' }}>
          <tr>
            <th style={th}>Droit</th>
            <th style={th}>Qui l'a</th>
            <th style={th}>D'où il vient</th>
            <th style={{ ...th, textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {mod.permissions.map((p) => {
            const porteParUneCible = (p.roleIds || []).length > 0 || (p.groupIds || []).length > 0;
            const tousLesRolesLOnt = selectedRoleIds.length > 0
              && selectedRoleIds.every((r) => (p.roleIds || []).includes(r));
            // « Tout le monde l'a » doit inclure les rôles cochés, sinon le
            // bouton « Donner » reste actif pour une opération qui ne ferait
            // rien, et « Retirer » est grisé sur une ligne de rôle bien réelle.
            const everyone = (p.total === 0 || p.holders >= p.total)
              && (selectedRoleIds.length === 0 || tousLesRolesLOnt)
              && (p.total > 0 || selectedRoleIds.length > 0);
            const nobody = p.holders === 0 && !porteParUneCible;
            return (
              <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '7px 8px' }}>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{p.label}</div>
                  {p.description && <div style={{ color: '#94a3b8', fontSize: 11 }}>{p.description}</div>}
                  {p.lockedReason && <div style={{ color: '#92400e', fontSize: 11 }}>🔒 {p.lockedReason}</div>}
                </td>
                <td style={{ padding: '7px 8px', color: everyone ? '#15803d' : '#475569', whiteSpace: 'nowrap' }}>
                  {p.total === 0 && (selectedRoleIds.length > 0 || selectedGroupIds.length > 0)
                    ? 'aucun porteur aujourd’hui'
                    : coverageText(p.holders, p.total)}
                </td>
                <td style={{ padding: '7px 8px', color: '#64748b' }}>
                  {sourceText(p, cibleLabels)}
                  {p.sources.role > 0 && (
                    <div style={{ color: '#92400e', fontSize: 11 }}>
                      Vient du rôle : le retirer du groupe ne suffirait pas.
                    </div>
                  )}
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => onRemove(p)}
                    disabled={nobody || !!p.lockedReason}
                    title={p.lockedReason || (nobody ? 'Personne dans la sélection ne l\'a' : 'Retirer ce droit')}
                    style={{ ...btn, color: '#991b1b', borderColor: '#fecaca', opacity: (nobody || p.lockedReason) ? 0.45 : 1, cursor: (nobody || p.lockedReason) ? 'not-allowed' : 'pointer' }}
                  >
                    Retirer
                  </button>{' '}
                  <button
                    onClick={() => onGrant(p)}
                    disabled={everyone && !groupsSelected}
                    title={everyone
                      ? (groupsSelected
                        ? "Toute la sélection l'a déjà — mais on peut encore l'ajouter au groupe"
                        : "Toute la sélection l'a déjà")
                      : 'Donner ce droit'}
                    style={{ ...btn, color: '#166534', borderColor: '#bbf7d0', opacity: (everyone && !groupsSelected) ? 0.45 : 1, cursor: (everyone && !groupsSelected) ? 'not-allowed' : 'pointer' }}
                  >
                    {p.holders > 0 ? 'Remettre à tous' : 'Donner'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// Confirmation — rien ne s'écrit sans elle.
// Le plan affiché vient du SERVEUR (dryRun), pas d'un calcul du navigateur :
// c'est le seul moyen d'annoncer un chiffre qui sera vraiment celui appliqué.
// =====================================================================
function ConfirmDialog({ pending, view, groups, roles, selectedUserIds, selectedGroupIds, selectedRoleIds, onCancel, onDone }) {
  const [alsoGroup, setAlsoGroup] = useState(false);
  const [reason, setReason] = useState('');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  const isRemove = pending.mode === 'REMOVE';
  const canTouchGroup = groups.length > 0;
  const touchesRole = roles.length > 0;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Sans cela, « Calcul de l'effet exact… » s'affiche au-dessus des chiffres
    // du plan PRÉCÉDENT : on lit des nombres qui ne correspondent plus à la
    // case qu'on vient de cocher.
    setPlan(null);
    permsApi.bulkApply({
      userIds: selectedUserIds,
      groupIds: selectedGroupIds,
      roleIds: selectedRoleIds,
      permissionIds: pending.permissionIds,
      mode: pending.mode,
      alsoGroup: alsoGroup && canTouchGroup,
      dryRun: true,
    })
      .then((d) => { if (alive) { setPlan(d.plan); setError(null); } })
      .catch((e) => { if (alive) { setError(errMsg(e)); setPlan(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pending, alsoGroup, canTouchGroup, selectedUserIds, selectedGroupIds, selectedRoleIds]);

  // Ce que la sélection GARDE : les autres modules qu'elle possède encore.
  const keptModules = useMemo(() => {
    const touched = new Set((view?.modules || [])
      .filter((m) => m.permissions.some((p) => pending.permissionIds.includes(p.id)))
      .map((m) => m.category));
    // `moduleHeld` et non `holders > 0` : avec un rôle encore inoccupé, tous
    // les compteurs de personnes valent zéro et la fenêtre annonçait
    // « aucun autre module possédé » alors que la ligne de rôle en portait
    // plusieurs.
    return (view?.modules || []).filter((m) => moduleHeld(m) && !touched.has(m.category));
  }, [view, pending]);

  async function apply() {
    setApplying(true);
    try {
      const d = await permsApi.bulkApply({
        userIds: selectedUserIds,
        groupIds: selectedGroupIds,
        roleIds: selectedRoleIds,
        permissionIds: pending.permissionIds,
        mode: pending.mode,
        alsoGroup: alsoGroup && canTouchGroup,
        reason: reason.trim() || undefined,
        dryRun: false,
      });
      // Le message doit décrire ce qui s'est PASSÉ. Quand seule la ligne du
      // groupe bouge, aucune personne n'est modifiée individuellement — dire
      // « appliqué à 0 personnes » laisserait croire qu'il ne s'est rien
      // produit, alors que tous les futurs membres du groupe sont concernés.
      const n = d.plan?.affectedPeople ?? 0;
      const g = d.plan?.groupChanges ?? 0;
      const r = d.plan?.roleChanges ?? 0;
      const verbe = isRemove ? 'Retrait' : 'Ajout';
      // Nommer la PORTÉE, pas seulement le nombre : « appliqué à 0 personne »
      // après une modification de rôle laisserait croire qu'il ne s'est rien
      // passé, alors que tous les futurs porteurs du rôle sont concernés.
      const portee = [r > 0 && 'au rôle', g > 0 && 'au groupe'].filter(Boolean).join(' et ');
      let message;
      if (d.applied === false) message = 'Rien n\'a changé : la sélection était déjà dans l\'état demandé.';
      else if (n === 0 && (g > 0 || r > 0)) message = `${verbe} appliqué ${portee}. Personne n'est modifiée individuellement, mais le réglage vaut pour les porteurs actuels et futurs.`;
      else message = `${verbe} appliqué à ${n} personne${n > 1 ? 's' : ''}${portee ? `, et ${portee}` : ''}.`
        + (r > 0
          ? ' Attention : un réglage de rôle vaut aussi pour les personnes qui le recevront plus tard.'
          : ' Vous pouvez revenir en arrière depuis cet écran, droit par droit.');
      onDone(message);
    } catch (e) {
      setError(errMsg(e));
      setApplying(false);
    }
  }

  const nothingToDo = plan && plan.affectedPeople === 0 && plan.groupChanges === 0 && plan.roleChanges === 0;

  return (
    <div style={overlay} role="dialog" aria-modal="true">
      <div style={{ ...dialog, borderTop: `5px solid ${isRemove ? '#dc2626' : '#16a34a'}` }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{pending.title}</h2>
        <div style={{ ...muted, marginBottom: 12 }}>
          Vérifiez avant d'appliquer. Rien n'est enregistré tant que vous n'avez pas confirmé.
        </div>

        {loading && <div style={{ padding: 20 }}>Calcul de l'effet exact…</div>}
        {error && <Banner tone="error">{error}</Banner>}

        {plan && (
          <>
            <Section title={isRemove ? 'Ce qui est retiré' : 'Ce qui est donné'}>
              <ul style={ul}>
                {plan.permissions.map((p) => <li key={p.id}>{p.label}</li>)}
              </ul>
              {pending.wholeModule && plan.permissions.length > 1 && (
                <div style={{ color: '#92400e', marginTop: 4 }}>
                  Donner un module donne <strong>tous</strong> les droits ci-dessus, y compris
                  ceux que personne n'avait jusqu'ici. Ce n'est donc pas l'exact inverse d'un
                  retrait. Pour n'en donner qu'un, fermez cette fenêtre et ouvrez le module.
                </div>
              )}
            </Section>

            <Section title="Ce qui est conservé">
              {keptModules.length === 0
                ? <div style={muted}>Aucun autre module n'est possédé par cette sélection.</div>
                : <div>
                    {keptModules.length} autre{keptModules.length > 1 ? 's' : ''} module{keptModules.length > 1 ? 's' : ''} :{' '}
                    {keptModules.map((m) => moduleLook(m.category).label).join(' · ')}
                  </div>}
              {/* Ne promettre que ce que le serveur garantit vraiment : le
                  module entier et les trois droits « ses propres feuilles ».
                  Un droit d'administration des feuilles, lui, est retirable —
                  et c'est justement l'écran où on le retire. */}
              {pending.category === 'timesheets' && isRemove ? (
                <div style={{ color: '#166534', marginTop: 4 }}>
                  « Voir » et « créer » ses propres feuilles de temps ne peuvent pas être
                  retirés : sans eux, la personne ne peut plus rien encoder. Les autres droits
                  du module — modifier ou supprimer les siennes, administrer celles des autres —
                  restent retirables.
                </div>
              ) : pending.category === 'timesheets' ? (
                <div style={{ color: '#166534', marginTop: 4 }}>
                  Rien n'est retiré ici : cette opération ne fait qu'ajouter.
                </div>
              ) : (
                <div style={{ color: '#166534', marginTop: 4 }}>
                  Le module Feuilles de temps n'est pas touché par cette opération.
                </div>
              )}
            </Section>

            {plan.roleChanges > 0 && (
              <div style={{
                background: ROLE_TONE.bg, border: `2px solid ${ROLE_TONE.border}`,
                color: ROLE_TONE.strong, padding: '10px 12px', borderRadius: 8, marginBottom: 10,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  🎭 Cette opération modifie {plan.roleChanges > 1 ? 'des réglages de rôle' : 'un réglage de rôle'} —
                  pas seulement les personnes cochées
                </div>
                {plan.roleReach.filter((r) => r.changes > 0).map((r) => (
                  <div key={r.id} style={{ marginTop: 2 }}>
                    <strong>{r.label}</strong> : {r.holders} personne{r.holders > 1 ? 's' : ''} porte{r.holders > 1 ? 'nt' : ''} ce rôle
                    {r.notPicked > 0
                      ? <>, dont <strong>{r.notPicked} que vous n'avez pas cochée{r.notPicked > 1 ? 's' : ''}</strong> et qui {r.notPicked > 1 ? 'seront touchées' : 'sera touchée'} quand même</>
                      : <> (toutes cochées)</>}.
                  </div>
                ))}
                <div style={{ marginTop: 4 }}>
                  Et <strong>toutes les personnes qui recevront ce rôle plus tard</strong> :
                  une embauche, un changement de poste. Ce réglage-là ne se voit nulle part
                  sur la fiche d'une personne.
                </div>
              </div>
            )}

            <Section title="Pour qui">
              <div>
                <strong>{plan.affectedPeople}</strong> personne{plan.affectedPeople > 1 ? 's' : ''} sur {plan.actionablePeople} concernée{plan.actionablePeople > 1 ? 's' : ''} {plan.affectedPeople > 1 ? 'seront modifiées' : 'sera modifiée'}.
                {plan.actionablePeople !== plan.selectedPeople && (
                  <span style={muted}> ({plan.selectedPeople} cochée{plan.selectedPeople > 1 ? 's' : ''} au total ; les cas écartés sont détaillés ci-dessous.)</span>
                )}
              </div>
              {plan.unaffectedPeople > 0 && (
                <div style={muted}>
                  {plan.unaffectedPeople} {plan.unaffectedPeople > 1 ? 'sont déjà' : 'est déjà'} dans l'état demandé : rien ne changera pour {plan.unaffectedPeople > 1 ? 'elles' : 'elle'}.
                </div>
              )}
              {plan.inactiveInSelection > 0 && (
                <div style={muted}>
                  Dont {plan.inactiveInSelection} compte{plan.inactiveInSelection > 1 ? 's' : ''} désactivé{plan.inactiveInSelection > 1 ? 's' : ''} :
                  {plan.inactiveInSelection > 1 ? ' ils sont traités' : ' il est traité'} comme les autres, pour que
                  {plan.inactiveInSelection > 1 ? ' leurs droits soient corrects' : ' ses droits soient corrects'} au retour.
                </div>
              )}
              {plan.skippedSelf?.length > 0 && (
                <div style={{ color: '#92400e', marginTop: 4 }}>
                  Vous faites partie de la sélection : vos propres droits ne sont pas modifiés ici.
                  {isRemove
                    ? ' Se retirer un droit depuis cet écran rendrait le retour en arrière impossible.'
                    : ' On ne s\'accorde pas un droit à soi-même depuis cet écran.'}
                  {' '}Demandez à quelqu'un d'autre de le faire.
                </div>
              )}
              {plan.selfAffectedByTarget && (
                <div style={{ color: '#991b1b', marginTop: 4, fontWeight: 600 }}>
                  Attention : vous portez un des rôles modifiés, ou vous êtes membre d'un des
                  groupes modifiés. Cette opération changera aussi VOS droits — aucune
                  dérogation n'est écrite sur vous, mais le rôle et le groupe, eux, vous concernent.
                </div>
              )}
              {plan.skippedUnknown > 0 && (
                <div style={{ color: '#92400e', marginTop: 4 }}>
                  {plan.skippedUnknown} compte{plan.skippedUnknown > 1 ? 's' : ''} de la sélection n'existe{plan.skippedUnknown > 1 ? 'nt' : ''} plus
                  (supprimé{plan.skippedUnknown > 1 ? 's' : ''} entre-temps). Rechargez l'écran pour repartir d'une liste à jour.
                </div>
              )}
              {plan.skippedSuperAdmins.length > 0 && (
                <div style={{ color: '#92400e', marginTop: 4 }}>
                  Ignorée{plan.skippedSuperAdmins.length > 1 ? 's' : ''} — accès total :{' '}
                  {trierPersonnes(plan.skippedSuperAdmins).map((s) => s.name).join(', ')}. Une restriction n'a aucun effet sur {plan.skippedSuperAdmins.length > 1 ? 'ces comptes' : 'ce compte'}.
                </div>
              )}
              {groups.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {/* Sans effectif ici : celui de la colonne de gauche compte
                      tout le monde (accès totaux, vous-même), alors que le
                      chiffre juste au-dessus les écarte. Deux nombres côte à
                      côte qui se contredisent valent moins qu'un seul. */}
                  Groupes concernés : {groups.map((g) => g.name).join(' · ')}
                </div>
              )}
              {plan.details.length > 0 && (
                <div style={{ ...muted, marginTop: 4, maxHeight: 90, overflowY: 'auto' }}>
                  {trierPersonnes(plan.details).map((d) => d.name + marqueurInactif(d)).join(' · ')}
                </div>
              )}
            </Section>

            <div style={noteBox}>
              <strong>Comment c'est enregistré :</strong>{' '}
              {touchesRole
                ? <>le réglage du <em>rôle</em> lui-même (et non une dérogation par personne).
                  Aucune fiche individuelle ne portera la trace de ce changement — le journal, si.</>
                : <>une <em>dérogation par personne</em>.</>}
              {' '}C'est réversible : il suffit de refaire l'opération dans l'autre sens depuis cet écran.
              {plan.changedWithoutWrite > 0 && <>{' '}
                <strong>{plan.changedWithoutWrite} personne{plan.changedWithoutWrite > 1 ? 's' : ''}</strong>{' '}
                {plan.changedWithoutWrite > 1 ? 'changent' : 'change'} d'état sans qu'aucune ligne ne porte
                {plan.changedWithoutWrite > 1 ? ' leur nom' : ' son nom'} : {plan.changedWithoutWrite > 1 ? 'elles suivent' : 'elle suit'} le rôle ou le groupe.</>}
              {canTouchGroup && !alsoGroup && <> Le groupe lui-même n'est pas modifié —{' '}
                <strong>ceci ne touche pas les futures personnes ajoutées au groupe</strong>.</>}
              {canTouchGroup && alsoGroup && <> <strong>Le groupe lui-même est modifié</strong> :
                les personnes qui le rejoindront plus tard suivront ce nouveau réglage.</>}
            </div>

            {nothingToDo && pending.groupOnly && !alsoGroup && (
              <Banner tone="warn">
                Toutes les personnes sélectionnées possèdent déjà {plan.permissions.length > 1 ? 'ces droits' : 'ce droit'}.
                Pour que cette opération serve à quelque chose, cochez{' '}
                <strong>« Ajouter aussi au groupe »</strong> ci-dessous : le réglage vaudra alors
                pour les personnes qui rejoindront le groupe plus tard.
              </Banner>
            )}
            {nothingToDo && !(pending.groupOnly && !alsoGroup) && (
              <Banner tone="warn">
                Cette opération ne changerait rien : la sélection est déjà dans l'état demandé.
              </Banner>
            )}
          </>
        )}

        {/* La case et le motif restent AFFICHÉS même quand le calcul du plan
            a échoué : c'est souvent la case elle-même qui a provoqué le refus
            (droit manquant, auto-verrouillage). La cacher enfermerait
            l'utilisateur dans une fenêtre où plus rien n'est possible. */}
        {canTouchGroup && (
          <label style={{ ...checkRow, alignItems: 'flex-start', marginTop: 8, background: alsoGroup ? '#fff7ed' : 'transparent', borderRadius: 6, padding: 8 }}>
            <input type="checkbox" checked={alsoGroup} onChange={(e) => setAlsoGroup(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              <strong>{isRemove ? 'Retirer aussi du groupe' : 'Ajouter aussi au groupe'}</strong> ({groups.map((g) => g.name).join(', ')})
              <div style={muted}>
                Action plus forte : elle change le groupe lui-même, donc aussi pour les personnes
                qui le rejoindront plus tard. Sans cette case, seules les personnes listées ci-dessus sont touchées.
              </div>
              {alsoGroup && plan?.groupChanges > 0 && (
                <div style={{ color: '#9a3412', marginTop: 2 }}>
                  {plan.groupChanges} modification{plan.groupChanges > 1 ? 's' : ''} sur le{groups.length > 1 ? 's' : ''} groupe{groups.length > 1 ? 's' : ''}.
                </div>
              )}
              {alsoGroup && error && (
                <div style={{ color: '#991b1b', marginTop: 2 }}>
                  C'est peut-être cette option qui bloque : décochez-la pour appliquer la
                  modification personne par personne.
                </div>
              )}
            </span>
          </label>
        )}

        <div style={{ marginTop: 10 }}>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motif (facultatif, mais il sera repris dans le journal)"
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={btn} disabled={applying}>Annuler</button>
          <button
            onClick={apply}
            disabled={loading || applying || !plan || nothingToDo}
            style={{
              ...btn,
              background: isRemove ? '#dc2626' : '#16a34a', color: 'white', borderColor: 'transparent',
              opacity: (loading || applying || !plan || nothingToDo) ? 0.5 : 1,
              cursor: (loading || applying || !plan || nothingToDo) ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {applying ? 'Application…' : (isRemove ? 'Oui, retirer' : 'Oui, donner')}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Petits blocs de présentation
// =====================================================================
function PanelTitle({ children }) {
  return <h3 style={{ margin: 0, fontSize: 15, color: '#0f172a' }}>{children}</h3>;
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: .3, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#1e293b' }}>{children}</div>
    </div>
  );
}

function Banner({ tone = 'info', children }) {
  const tones = {
    info:    { bg: '#eff6ff', border: '#93c5fd', fg: '#1e3a8a' },
    warn:    { bg: '#fffbeb', border: '#fcd34d', fg: '#78350f' },
    error:   { bg: '#fef2f2', border: '#fca5a5', fg: '#991b1b' },
    success: { bg: '#f0fdf4', border: '#86efac', fg: '#166534' },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.fg, padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
      {children}
    </div>
  );
}

const panel = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 };
const muted = { color: '#64748b', fontSize: 12 };
const checkRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer', fontSize: 13 };
const countPill = { fontSize: 11, color: '#475569', background: '#f1f5f9', borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' };
const tileGrid = { display: 'flex', flexWrap: 'wrap', gap: 14, paddingTop: 6 };
const th = { padding: '7px 8px', textAlign: 'left', fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #e2e8f0' };
const btn = { padding: '5px 11px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontSize: 12 };
const inputStyle = { padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', fontSize: 13, boxSizing: 'border-box' };
const ul = { margin: '2px 0 0 18px', padding: 0 };
const noteBox = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, fontSize: 12, color: '#334155', marginTop: 6 };
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const dialog = {
  background: 'white', borderRadius: 12, padding: 20, width: 1080, maxWidth: '96vw',
  maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.3)', fontSize: 13,
};
